import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Card,
  Chip,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  useDeleteGapJustificationForTreatments,
  useGapJustificationForTreatments,
} from "../../../stores/Appointment/appointment.mutation";
import { GapJustificationDto } from "../../../api-client";
import { observer } from "mobx-react";
import { Delete } from "@mui/icons-material";
import Controls from "../../../atoms/Controls";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import dayjs from "dayjs";
import {
  INSURANCE_JUSTIFICATION_REASONS,
  findInsuranceReasonByValue,
} from "../../../helpers/gapJustificationReasons";
import notificationStore from "../../../stores/Notification/notificationStore";

const reasonOptions = INSURANCE_JUSTIFICATION_REASONS.map((reason) => ({
  value: reason.value,
  label: reason.value,
}));

interface GapJustificationDialogProps {
  open: boolean;
  onClose: () => void;
  gapInDays: number;
  gapJustificationData: GapJustificationDto[];
  rxData?: any;
  initialGapJustificationData?: GapJustificationDto;
}

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("DD.MM.YYYY") : "";

// Drop an index from the set of "currently editing" rows and shift the
// remaining higher indices down by one, so editing state stays attached to the
// right rows after a deletion.
const removeEditingIndex = (set: Set<number>, removedIdx: number) => {
  const next = new Set<number>();
  set.forEach((i) => {
    if (i === removedIdx) return;
    next.add(i > removedIdx ? i - 1 : i);
  });
  return next;
};

export const GapJustificationDialog: React.FC<GapJustificationDialogProps> = observer(
  ({ open, onClose, gapInDays, gapJustificationData, rxData, initialGapJustificationData }) => {
    const { mutate: addUpdateGapJustification } = useGapJustificationForTreatments();
    const { mutate: deleteGapJustification } = useDeleteGapJustificationForTreatments();
    const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [localJustifications, setLocalJustifications] = useState<GapJustificationDto[]>([]);
    // Indices of the rows currently in edit/add mode. Editing is per-row: adding
    // a new justification or editing one existing entry must not flip the other
    // saved entries into edit mode.
    const [editingIdxs, setEditingIdxs] = useState<Set<number>>(new Set());

    const savedCount = (gapJustificationData || []).filter((j) => j.id).length;
    const isEditing = editingIdxs.size > 0;

    // Initialise the dialog state only when it (re)opens. Doing it here rather
    // than on every gapJustificationData change avoids wiping in-progress edits
    // when the parent re-renders with a fresh array reference.
    useEffect(() => {
      if (!open) return;
      const data = gapJustificationData || [];
      const saved = data.filter((j) => j.id);
      if (saved.length > 0) {
        // There are existing, persisted justifications -> open read-only.
        setLocalJustifications(data);
        setEditingIdxs(new Set());
      } else {
        // Nothing saved yet -> go straight to the add flow with one blank row.
        const initial = data.length
          ? data
          : initialGapJustificationData
            ? [initialGapJustificationData]
            : [];
        setLocalJustifications(initial);
        setEditingIdxs(new Set(initial.map((_, i) => i)));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleDelete = (idx: number) => {
      if (!localJustifications[idx]?.id) {
        setLocalJustifications((prev) => prev.filter((_, i) => i !== idx));
        setEditingIdxs((prev) => removeEditingIndex(prev, idx));
        return;
      }
      setDeleteIdx(idx);
      setShowDeleteDialog(true);
    };

    const confirmDelete = () => {
      if (deleteIdx === null) return;
      const justification = localJustifications[deleteIdx];
      if (justification?.id) {
        deleteGapJustification({
          gapId: justification.id,
          patientId: justification.patientId!,
          rxId: rxData?.id!,
        });
      }
      setLocalJustifications((prev) => prev.filter((_, i) => i !== deleteIdx));
      setEditingIdxs((prev) => removeEditingIndex(prev, deleteIdx));
      setDeleteIdx(null);
      setShowDeleteDialog(false);
    };

    const cancelDelete = () => {
      setDeleteIdx(null);
      setShowDeleteDialog(false);
    };

    const handleAdd = () => {
      const newItem = (initialGapJustificationData || {}) as GapJustificationDto;
      const newIdx = localJustifications.length;
      setLocalJustifications((prev) => [...prev, newItem]);
      setEditingIdxs((prev) => new Set(prev).add(newIdx));
    };

    const handleEnterEdit = (idx: number) => {
      setEditingIdxs((prev) => new Set(prev).add(idx));
    };

    const handleChange = (idx: number, field: keyof GapJustificationDto, value: any) => {
      setLocalJustifications((prev) =>
        prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)),
      );
    };

    const handleSave = () => {
      // Every justification must carry an insurance-valid reason code.
      const missingReason = localJustifications.some((j) => !j.reason);
      if (missingReason) {
        notificationStore.showMessage(
          "Bitte wählen Sie für jede Begründung eine versicherungsgültige Begründung aus.",
          "warning",
        );
        return;
      }
      addUpdateGapJustification({
        gapJustificationDto: localJustifications,
        rxId: rxData?.id!,
      });
      onClose();
    };

    // Cancel any in-progress edit/add: discard local changes back to the saved
    // data. Close only when there is nothing saved to fall back to.
    const handleCancelEdit = () => {
      setLocalJustifications(gapJustificationData || []);
      setEditingIdxs(new Set());
      if (savedCount === 0) {
        onClose();
      }
    };

    const handleDialogClose = () => {
      setLocalJustifications(gapJustificationData || []);
      setEditingIdxs(new Set());
      onClose();
    };

    const renderEditCard = (justification: GapJustificationDto, idx: number) => (
      <Card key={justification.id || `edit-${idx}`} sx={{ padding: 2, marginBottom: 2 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography variant="subtitle2">Lückenbegründung {idx + 1}</Typography>
            <Chip
              size="small"
              label={justification.id ? "Gespeichert" : "Neu"}
              color={justification.id ? "success" : "default"}
              variant="outlined"
            />
          </div>
          <IconButton onClick={() => handleDelete(idx)} size="small" aria-label="Löschen">
            <Delete fontSize="small" />
          </IconButton>
        </div>

        {/* Label above, error below — the MUI control rendered its error text
            inside the floating label, where it landed on top of the selected
            value. Stacking them cannot overlap. */}
        <div className="mt-2 space-y-1.5">
          <Label className="text-sm">
            Versicherungsgültige Begründung
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <Select
            value={justification.reason || ""}
            onValueChange={(value) => handleChange(idx, "reason", value)}
          >
            <SelectTrigger
              aria-label="Versicherungsgültige Begründung"
              aria-invalid={!justification.reason}
              className={!justification.reason ? "border-destructive" : undefined}
            >
              <SelectValue placeholder="Begründung wählen" />
            </SelectTrigger>
            {/* Radix portals this to document.body with z-50, which is *below*
                MUI's Dialog (z-index 1300) — the list opened invisibly behind
                the dialog. twMerge lets this className win over the base z-50. */}
            <SelectContent className="z-[1400]">
              {reasonOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!justification.reason && (
            <p className="text-sm text-destructive">Erforderlich</p>
          )}
        </div>

        <Controls.DatePicker
          name="startDate"
          label="Startdatum"
          value={justification.startDate ? dayjs(justification.startDate).format("YYYY-MM-DD") : ""}
          onChange={(e: any) => handleChange(idx, "startDate", e.target.value)}
          formatDate="dd.MM.yyyy"
        />

        <Controls.DatePicker
          name="endDate"
          label="Enddatum"
          value={justification.endDate ? dayjs(justification.endDate).format("YYYY-MM-DD") : ""}
          onChange={(e: any) => handleChange(idx, "endDate", e.target.value)}
          formatDate="dd.MM.yyyy"
        />

        <Controls.TextArea
          name="description"
          label="Vermerk"
          value={justification.description || ""}
          onChange={(e: any) => handleChange(idx, "description", e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
          multiline
          minRows={2}
        />
      </Card>
    );

    const renderDisplayCard = (justification: GapJustificationDto, idx: number) => (
      <Card key={justification.id || `view-${idx}`} sx={{ padding: 2, marginBottom: 2 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle2">Lückenbegründung {idx + 1}</Typography>
          <div>
            <Tooltip title="Bearbeiten">
              <IconButton
                onClick={() => handleEnterEdit(idx)}
                size="small"
                aria-label="Bearbeiten"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Löschen">
              <IconButton onClick={() => handleDelete(idx)} size="small" aria-label="Löschen">
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {(justification.startDate || justification.endDate) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {[justification.startDate, justification.endDate]
              .filter(Boolean)
              .map((d) => formatDate(d))
              .join(" – ")}
          </Typography>
        )}

        {justification.reason ? (
          <Chip
            size="small"
            label={findInsuranceReasonByValue(justification.reason)?.value || justification.reason}
            sx={{
              mt: 1,
              fontWeight: 600,
              backgroundColor: "grey.200",
              color: "text.primary",
            }}
          />
        ) : (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Diese Begründung ist nicht versicherungsgültig (keine Begründung hinterlegt).
          </Alert>
        )}

        {justification.description && (
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
            {justification.description}
          </Typography>
        )}
      </Card>
    );

    return (
      <>
        <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
          <DialogTitle>Lückenbegründung</DialogTitle>

          <DialogContent sx={{ minWidth: 350 }}>
            {savedCount > 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Für diese Lücke von {gapInDays} Tagen{" "}
                {savedCount === 1
                  ? "ist bereits 1 Begründung"
                  : `sind bereits ${savedCount} Begründungen`}{" "}
                hinterlegt.
              </Alert>
            ) : (
              <Typography mb={2}>
                Bitte geben Sie eine Begründung für die Lücke von {gapInDays} Tagen ein.
              </Typography>
            )}

            {localJustifications.map((justification, idx) =>
              editingIdxs.has(idx)
                ? renderEditCard(justification, idx)
                : renderDisplayCard(justification, idx),
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <Tooltip title="Neue Lückenbegründung hinzufügen">
                <Button color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
                  Neue Lückenbegründung
                </Button>
              </Tooltip>
            </div>
          </DialogContent>

          <DialogActions>
            {isEditing ? (
              <>
                <Button onClick={handleCancelEdit}>Abbrechen</Button>
                <Button onClick={handleSave} color="primary" variant="contained">
                  Speichern
                </Button>
              </>
            ) : (
              <Button onClick={handleDialogClose}>Schließen</Button>
            )}
          </DialogActions>
        </Dialog>
        {/* Delete confirmation dialog */}
        <Dialog open={showDeleteDialog} onClose={cancelDelete}>
          <DialogTitle>Löschen bestätigen</DialogTitle>
          <DialogContent>
            <Typography>Möchten Sie diese Lückenbegründung wirklich löschen?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete}>Abbrechen</Button>
            <Button onClick={confirmDelete} variant="contained">
              Löschen
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);
